/**
 * The vocabulary of automation, in one place.
 *
 * Runs, steps, dispatches and flows each carry a status string from the server. The words
 * overlap (`running`, `completed`, `failed`, `paused`) and where they overlap they mean the
 * same thing, so one table serves all four rather than four tables that can disagree about
 * what "failed" looks like.
 *
 * The labels are literal `t()` calls so the extractor can see them; the tone is what stops a
 * finished run and a burnt one from reading the same at a glance.
 */
export type StatusTone = "neutral" | "accent" | "success" | "warning" | "danger";

type Translate = (key: string) => string;

export interface StatusPresentation {
  label: string;
  tone: StatusTone;
}

export function statusPresentation(status: string, t: Translate): StatusPresentation {
  switch (status) {
    // Not started yet.
    case "requested":
      return { label: t("Requested"), tone: "neutral" };
    case "queued":
      return { label: t("Queued"), tone: "neutral" };
    case "pending":
      return { label: t("Pending"), tone: "neutral" };
    case "draft":
      return { label: t("Draft"), tone: "neutral" };

    // Under way.
    case "running":
      return { label: t("Running"), tone: "accent" };
    case "observing":
      return { label: t("Observing"), tone: "accent" };
    case "claimed":
      return { label: t("Claimed"), tone: "accent" };
    case "active":
      return { label: t("Active"), tone: "accent" };

    // Stopped, but not over.
    case "paused":
      return { label: t("Paused"), tone: "warning" };
    case "skipped":
      return { label: t("Skipped"), tone: "warning" };
    case "expired":
      return { label: t("Expired"), tone: "warning" };

    // Finished.
    case "completed":
      return { label: t("Completed"), tone: "success" };
    // It answered, but without inputs it was told to use. Never plain "completed".
    case "completed-degraded":
      return { label: t("Completed without some sources"), tone: "warning" };
    case "failed":
      return { label: t("Failed"), tone: "danger" };
    case "cancelled":
      return { label: t("Cancelled"), tone: "neutral" };
    case "archived":
      return { label: t("Archived"), tone: "neutral" };

    // W2 — step statuses (RunStepStatusWire) and dispatch/task-board words not already covered.
    case "requested-step":
      return { label: t("Requested"), tone: "neutral" };
    case "claimed-dispatch":
      return { label: t("Claimed"), tone: "accent" };
    case "sent":
      return { label: t("Sent"), tone: "success" };
    case "purged":
      return { label: t("Purged"), tone: "neutral" };

    default:
      return { label: status, tone: "neutral" };
  }
}

/**
 * Run health, straight from the server's own three-level vocabulary (`TaskHealth.level`).
 * Kept apart from `statusPresentation` — health is an infrastructure rollup, never a run state
 * (§A.1) — so the two must never share one lookup a reviewer could accidentally conflate.
 */
export function healthTone(level: "ok" | "warn" | "error"): StatusTone {
  return level === "error" ? "danger" : level === "warn" ? "warning" : "success";
}

export function healthColor(level: "ok" | "warn" | "error"): string {
  return level === "error" ? "#d64545" : level === "warn" ? "#d9822b" : "#1f9d55";
}

/**
 * Run status colours, kept literal (not Tailwind tokens) because they are shared with the
 * run-list pill, the analytics band's bar charts and — eventually — the run viewer's canvas,
 * none of which read the same CSS variables. `completed-degraded` is deliberately amber, never
 * folded into `completed` green or `failed` red (§A.4).
 */
export const RUN_STATUS_COLORS: Record<string, string> = {
  requested: "#8a8f98",
  running: "#2f80ed",
  observing: "#2f80ed",
  paused: "#d9822b",
  completed: "#1f9d55",
  "completed-degraded": "#b7791f",
  failed: "#d64545",
  cancelled: "#8a8f98",
};

export const RUN_KIND_LABELS: Record<string, string> = {
  digest: "Task",
  flow: "Flow",
  "pulse-advisor": "Advisor",
};

/** F-6: three RunKindWire values kms never learned. Fall back to the raw string, never blank. */
export function runKindLabel(kind: string, t: Translate): string {
  const key = RUN_KIND_LABELS[kind];
  return key ? t(key) : kind;
}

/** Log lines carry their own severity, which is not a status but reads like one. */
export function logLevelTone(level: string): StatusTone {
  return level === "error" ? "danger" : level === "warn" ? "warning" : "neutral";
}
